import React, { Fragment, useRef, useState, useEffect } from 'react';
import { translate } from '../Services/translation';
import FeaturedItemsDropdown, { MobileFeaturedItemsDropdown } from './FeaturedItemsDropdown';


const DesktopMegaMenu = ({ links, contentLink, isExpanded, menuRef, onClose, onMouseLeave }) => {
    const [activeCategory, setActiveCategory] = useState(0);
    const categoryRefs = useRef([]);
    const rightMenuRef = useRef(null);

    useEffect(() => {
        if (isExpanded) {
            setActiveCategory(0);
        }
    }, [isExpanded]);

    const hasNameOrChildren = (link) => link.name || (link.links || []).length > 0;

    const activeSubLinks = links[activeCategory]?.links || [];

    const focusActiveCategory = () => {
        const el = categoryRefs.current?.[activeCategory];
        if (el instanceof HTMLElement) {
            el.focus();
        }
    };

    const focusFirstRightLink = () => {
        const el = rightMenuRef.current?.querySelector('a.navbar__megamenu-link, a, button');
        if (el instanceof HTMLElement) {
            el.focus();
        }
    };

    const moveActiveCategory = (fromIndex, direction) => {
        const max = links.length;
        if (max === 0) return;

        let next = fromIndex;
        for (let tries = 0; tries < max; tries++) {
            next = (next + direction + max) % max;
            if (hasNameOrChildren(links[next])) break;
        }

        setActiveCategory(next);
        queueMicrotask(() => {
            categoryRefs.current?.[next]?.focus();
        });
    };

    return (
        <div
            className="navbar__megamenu"
            ref={menuRef}
            aria-label={contentLink ? `${contentLink.name} submenu` : "Navigation menu"}
            onMouseLeave={onMouseLeave}
        >
            <div className="navbar__megamenu-container">
                <div className="navbar__megamenu-left">
                    <div className="navbar__megamenu-title" dangerouslySetInnerHTML={{ __html: contentLink?.name }} />
                    <ul className="navbar__megamenu-categories">
                        {links.map((link, i) =>
                            hasNameOrChildren(link) && (
                                <li
                                    key={i}
                                    className={`navbar__megamenu-category ${activeCategory === i ? 'navbar__megamenu-category--active' : ''}`}
                                    onMouseEnter={() => setActiveCategory(i)}
                                >
                                    <a
                                        ref={(el) => {
                                            categoryRefs.current[i] = el;
                                        }}
                                        className="navbar__megamenu-category-btn"
                                        href={link.url || '#'}
                                        onFocus={() => setActiveCategory(i)}
                                        tabIndex={-1}
                                        onKeyDown={(e) => {
                                            if (e.key === 'ArrowDown') {
                                                e.preventDefault();
                                                setActiveCategory(i);
                                                moveActiveCategory(i, 1);
                                            }

                                            if (e.key === 'ArrowUp') {
                                                e.preventDefault();
                                                setActiveCategory(i);
                                                moveActiveCategory(i, -1);
                                            }

                                            if (e.key === 'ArrowRight') {
                                                e.preventDefault();
                                                setActiveCategory(i);
                                                focusFirstRightLink();
                                            }
                                        }}
                                        dangerouslySetInnerHTML={{ __html: link.name }}
                                        aria-expanded={activeCategory === i}
                                    />
                                </li>
                            )
                        )}
                    </ul>
                </div>

                <div className="navbar__megamenu-right" ref={rightMenuRef}>
                    {links[activeCategory] && (
                        <div className="navbar__megamenu-subcategory-title" dangerouslySetInnerHTML={{ __html: links[activeCategory].name }} />
                    )}
                    <ul className="navbar__megamenu-links">
                        {activeSubLinks.map((subLink, j) =>
                            hasNameOrChildren(subLink) && (
                                <li key={j} className="navbar__megamenu-link-item">
                                    <a
                                        href={subLink.url || '#'}
                                        className="navbar__megamenu-link"
                                        tabIndex={-1}
                                        onKeyDown={(e) => {
                                            if (e.key === 'ArrowLeft') {
                                                e.preventDefault();
                                                focusActiveCategory();
                                            }

                                            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                                const linksEls = rightMenuRef.current?.querySelectorAll('a.navbar__megamenu-link');
                                                if (!linksEls || linksEls.length === 0) return;

                                                const items = Array.from(linksEls).filter((x) => x instanceof HTMLElement);
                                                const currentIndex = items.indexOf(e.currentTarget);
                                                if (currentIndex === -1) return;

                                                e.preventDefault();

                                                const dir = e.key === 'ArrowDown' ? 1 : -1;
                                                const nextIndex = Math.max(0, Math.min(items.length - 1, currentIndex + dir));
                                                items[nextIndex]?.focus();
                                            }
                                        }}
                                        dangerouslySetInnerHTML={{ __html: subLink.name }}
                                    />
                                </li>
                            )
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

const MobileMenuItem = ({ links, contentLink, nested, featuredItems }) => {
    const menuRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleMenu = () => {
        menuRef.current?.classList.toggle('navbar__menu--show');
        setIsExpanded((prev) => !prev);
    };

    const closeMenu = () => {
        menuRef.current?.classList.remove('navbar__menu--show');
        setIsExpanded(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isExpanded && menuRef.current && !menuRef.current.contains(event.target)) {
                const toggleButton = event.target.closest('.navbar__icon--menu, .navbar__icon--open');
                if (!toggleButton) {
                    closeMenu();
                }
            }
        };

        if (isExpanded) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [isExpanded]);

    const hasNameOrChildren = (link) => link.name || (link.links || []).length > 0;

    const additionClass = contentLink?.attributes?.cssValue ?? null;
    const selectedClass = contentLink?.isSelected ? 'navbar__link--selected' : '';
    const hasChildrenClass = links.length > 0 ? 'has-children' : null;

    return (
        <Fragment>
            {!contentLink ? (
                <>
                    <button
                        className="navbar__link--block navbar__icon--menu navbar__icon"
                        onClick={toggleMenu}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleMenu();
                            }
                        }}
                        aria-label="Toggle navigation menu"
                        aria-expanded={isExpanded}
                        type="button"
                    > 
                        <span>
                            {translate('code.navigation.burger.title')}
                        </span>
                    </button>
                </>
            ) : (
                <Fragment>
                    <a
                        className={`navbar__link ${selectedClass} ${hasChildrenClass || ''} ${additionClass || ''}`}
                        href={contentLink.url || '#'}
                        dangerouslySetInnerHTML={{ __html: contentLink.name }}
                    ></a>
                    {links.length > 0 && (
                        <button
                            className="navbar__icon--caret-right navbar__icon navbar__icon--open"
                            onClick={toggleMenu}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleMenu();
                                }
                            }}
                            aria-expanded={isExpanded}
                            aria-label="Open submenu"
                            type="button"
                        ></button>
                    )}
                </Fragment>
            )}

            {links.length > 0 && (
                <div
                    className="navbar__menu"
                    ref={menuRef}
                    aria-label={contentLink ? `${contentLink.name} submenu` : "Navigation menu"}
                >
                    <ul>
                        <div className="navbar__menu-header">
                            {!contentLink ? (
                                <button
                                    className="navbar__icon navbar__icon--close"
                                    onClick={toggleMenu}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleMenu();
                                        }
                                    }}
                                    aria-label="Close navigation menu"
                                    type="button"
                                ></button>
                            ) : (
                                <Fragment>
                                    <button
                                        className="navbar__icon--caret-left navbar__icon"
                                        onClick={toggleMenu}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                toggleMenu();
                                            }
                                        }}
                                        aria-label="Back to parent menu"
                                        type="button"
                                    ></button>
                                    <button
                                        className="navbar__title"
                                        onClick={toggleMenu}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                toggleMenu();
                                            }
                                        }}
                                        dangerouslySetInnerHTML={{ __html: contentLink.name }}
                                        aria-label={`Back to parent menu from ${contentLink.name}`}
                                        type="button"
                                    ></button>
                                </Fragment>
                            )}
                        </div>
                        {!contentLink && featuredItems?.enabled && (
                            <MobileFeaturedItemsDropdown
                                label={featuredItems.label}
                                items={featuredItems.items}
                            />
                        )}
                        {links.map((link, i) =>
                            hasNameOrChildren(link) && (
                                <li className="navbar__item" key={i}>
                                    <MobileMenuItem
                                        links={link.links || []}
                                        contentLink={link}
                                        nested={true}
                                    />
                                </li>
                            )
                        )}
                    </ul>
                </div>
            )}
        </Fragment>
    );
};

const NavigationItem = ({ links = [], contentLink = null, nested = false, featuredItems = null }) => {
    const menuRef = useRef(null);
    const linkRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(() => {
        return window.matchMedia('(max-width: 1199px)').matches;
    });

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 1199px)');

        const checkMobile = () => {
            setIsMobile(mediaQuery.matches);
        };

        mediaQuery.addEventListener('change', checkMobile);
        return () => mediaQuery.removeEventListener('change', checkMobile);
    }, []);

    const openDesktopMenu = () => {
        if (isExpanded) return;
        setIsExpanded(true);
        menuRef.current?.classList.add('show-desktop-menu');
    };

    const toggleDesktopMenu = (event) => {
        if (event?.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
            return;
        }
        if (event?.type === 'keydown' && event.key === ' ') {
            event.preventDefault();
        }

        const newExpanded = !isExpanded;
        setIsExpanded(newExpanded);

        if (newExpanded) {
            menuRef.current?.classList.add('show-desktop-menu');
        } else {
            menuRef.current?.classList.remove('show-desktop-menu');
        }
    };

    const closeDesktopMenu = () => {
        setIsExpanded(false);
        menuRef.current?.classList.remove('show-desktop-menu');
    };

    const handleMouseLeave = (event, allowedTargetRef) => {
        if (
            event.relatedTarget instanceof Node &&
            allowedTargetRef.current?.contains(event.relatedTarget)
        ) {
            return;
        }

        closeDesktopMenu();
    };

    const handleKeyDown = (event) => {
        if (!isExpanded) return;
        if (event.key === 'Escape') {
            event.preventDefault();
            closeDesktopMenu();
            linkRef.current?.focus();
        }

        if (event.key === 'Tab') {
            const activeEl = document.activeElement;
            const insideThisItem =
                activeEl instanceof Node &&
                (linkRef.current?.contains(activeEl) || menuRef.current?.contains(activeEl));

            if (!insideThisItem) return;

            const currentLi = linkRef.current?.closest('li.navbar__item--level1');
            const direction = event.shiftKey ? -1 : 1;

            const findSibling = () => {
                let sib = direction === 1 ? currentLi?.nextElementSibling : currentLi?.previousElementSibling;
                while (sib) {
                    if (sib instanceof HTMLElement && sib.classList.contains('navbar__item--level1')) {
                        const target = sib.querySelector('a.navbar__link');
                        if (target instanceof HTMLElement) {
                            return target;
                        }
                    }
                    sib = direction === 1 ? sib.nextElementSibling : sib.previousElementSibling;
                }
                return null;
            };

            const siblingLink = findSibling();

            closeDesktopMenu();

            if (siblingLink) {
                event.preventDefault();
                siblingLink.focus();
            } else {
                linkRef.current?.focus();
            }
        }
    };

    useEffect(() => {
        if (isExpanded) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isExpanded]);

    useEffect(() => {
        const menuEl = menuRef.current;
        if (!menuEl) return;

        menuEl.setAttribute('aria-hidden', (!isExpanded).toString());

        const focusables = menuEl.querySelectorAll('a, button, input, select, textarea, [tabindex]');
        focusables.forEach((el) => {
            if (!(el instanceof HTMLElement)) return;

            if (!el.hasAttribute('data-orig-tabindex')) {
                const orig = el.getAttribute('tabindex');
                el.setAttribute('data-orig-tabindex', orig ?? '');
            }

            el.setAttribute('tabindex', '-1');
        });
    }, [isExpanded]);

    useEffect(() => {
        const handleFocusIn = (event) => {
            if (!isExpanded) return;
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (linkRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;
            closeDesktopMenu();
        };

        if (isExpanded) {
            document.addEventListener('focusin', handleFocusIn);
            return () => document.removeEventListener('focusin', handleFocusIn);
        }
    }, [isExpanded]);

    if (isMobile) {
        return <MobileMenuItem links={links} contentLink={contentLink} nested={nested} featuredItems={featuredItems} />;
    }

    const additionClass = contentLink?.attributes?.cssValue ?? null;
    const selectedClass = contentLink?.isSelected ? 'navbar__link--selected' : '';
    const hasChildrenClass = links.length > 0 ? 'has-children' : null;
    const hasNameOrChildren = (link) => link.name || (link.links || []).length > 0;

    if (!contentLink) {
        return (
            <Fragment>
                <div className="navbar__menu navbar__menu--desktop">
                    <ul className="navbar__menu-horizontal">
                        {featuredItems?.enabled && (
                            <FeaturedItemsDropdown
                                label={featuredItems.label}
                                items={featuredItems.items}
                            />
                        )}
                        {links.map((link, i) =>
                            hasNameOrChildren(link) && (
                                <li className="navbar__item navbar__item--level1" key={i}>
                                    <NavigationItem
                                        links={link.links || []}
                                        contentLink={link}
                                        nested={false}
                                    />
                                </li>
                            )
                        )}
                    </ul>
                </div>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <a
                ref={linkRef}
                className={`navbar__link ${selectedClass} ${hasChildrenClass || ''} ${additionClass || ''}`}
                tabIndex={0}
                dangerouslySetInnerHTML={{ __html: `<div class="content-link">${contentLink.name}</div>` }}
                aria-expanded={links.length > 0 ? isExpanded : undefined}
                aria-haspopup={links.length > 0 ? "true" : undefined}
                href={contentLink.url || '#'}
                onMouseEnter={links.length > 0 ? openDesktopMenu : undefined}
                onMouseLeave={links.length > 0 ? (e => handleMouseLeave(e, menuRef)) : undefined}
                onFocus={links.length > 0 ? openDesktopMenu : undefined}
                onKeyDown={(e) => {
                    if (links.length > 0 && e.key === 'ArrowDown') {
                        e.preventDefault();
                        openDesktopMenu();
                        const first = menuRef.current?.querySelector('a.navbar__megamenu-category-btn');
                        if (first instanceof HTMLElement) {
                            first.focus();
                        }
                    }
                }}
            ></a>

            {links.length > 0 && (
                <DesktopMegaMenu
                    links={links}
                    contentLink={contentLink}
                    isExpanded={isExpanded}
                    menuRef={menuRef}
                    onClose={closeDesktopMenu}
                    onMouseLeave={(e => handleMouseLeave(e, linkRef))}
                />
            )}
        </Fragment>
    );
};

export default NavigationItem;
